import React, { useState, useMemo } from 'react';
import { string, oneOf, func, bool } from 'prop-types';
import { Assemble } from '@one-thd/sui-icons';
import { Button, DrawerHeader, DrawerBody, Drawer } from '@one-thd/sui-atomic-components';
import { DualPathTooltip } from './DualPathTooltip';
import { ProductAddOnsPodStencil } from '../../common/product-add-ons-pod';
import { useTippyToggle } from '../../../hooks/useTippyToggle';
import { dualPathContactInformation } from '../../../utils/product-add-on-constants';

const DualPath = ({
  channel,
  itemId,
  onChange,
  category,
  serviceUrl,
  value
}) => {
  const [checked, setChecked] = useState(value);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const tooltipButtonRef = useTippyToggle(checked);
  const techshedUrl = `${serviceUrl}/?${itemId}&pageType=PIP#`;

  const DUAL_PATH_FORM_TITLE = {
    brio_inaisle_sfi_twht: 'Tankless Water Heater Installation',
    brio_inaisle_sfi_whtr: 'Water Heater Installation',
    BRIO_INAISLE_SFI_FENC: 'Fence Installation',
    dualpath_inaisle_sfi_generators: 'Generator Installation',
    dualpath_inaisle_sfi_windows: 'Window Installation',
    'dreamline-door-installation/44fbb1b75': 'Dreamline Shower Door Installation',
    'plumbing-repair-dp/9f2b4389b': 'Plumbing Repair',
    'water-heater-repair-dp/201c992c0': 'Water Heater Repair'
  };

  const headerDescription = DUAL_PATH_FORM_TITLE[category];

  const handleChange = (evt) => {
    LIFE_CYCLE_EVENT_BUS.trigger('dual-path.change', { isOptedIn: !checked, itemId });
    onChange({
      itemId,
      checked: evt.target.checked,
      addOnType: evt.target.value
    });
    setChecked(!checked);
  };

  const triggerAnalyticsEvent = () => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('dualPath.request-a-quote-action', { programCategory: category });
  };

  const requestAQuote = () => {
    setIsDrawerOpen(true);
    triggerAnalyticsEvent();
  };

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const subHeading = useMemo(() => {
    if (['brio_inaisle_sfi_twht', 'brio_inaisle_sfi_whtr'].includes(category)) {
      return (
        <div>Need help fast? Call&nbsp;
          <a className="sui-underline sui-text-primary" href={`tel:${dualPathContactInformation[category].tel}`}>
            {dualPathContactInformation[category].label}
          </a>
        </div>
      );
    }

    return <div>A local pro will take care of the job for you.</div>;
  }, [checked, category]);

  return (
    <>
      <ProductAddOnsPodStencil
        channel={channel}
        heading="Get Expert In-Home Installation"
        subHeading={subHeading}
        icon={<Assemble size="large" />}
        onChange={handleChange}
        tooltip={(
          <DualPathTooltip
            channel={channel}
            category={category}
            ref={tooltipButtonRef}
          />
        )}
        userOptions={[
          {
            id: 'item ID',
            value: 'dual-path',
            checked,
            label: 'Add In-Home Installation'
          }
        ]}
      >
        <div className={`${!checked && 'u--hide'}`}>
          <Button variant="primary" onClick={requestAQuote} fullWidth>
            Request a Quote
          </Button>
        </div>
      </ProductAddOnsPodStencil>
      {isDrawerOpen && (
        <Drawer
          open={isDrawerOpen}
          onClose={onDrawerClose}
        >
          <DrawerHeader title={headerDescription} onClose={onDrawerClose} />
          <DrawerBody>
            <iframe title="Request A Quote" src={techshedUrl} width="100%" height="900" />
          </DrawerBody>
        </Drawer>
      )}
    </>
  );
};

DualPath.displayName = 'ProductAddOns-DualPath';

DualPath.propTypes = {
  /** What view to render ['desktop' || 'mobile']  */
  channel: oneOf(['desktop', 'mobile']),
  itemId: string.isRequired,
  onChange: func.isRequired,
  serviceUrl: string.isRequired,
  category: string,
  value: bool
};

DualPath.defaultProps = {
  channel: 'desktop',
  category: 'brio_inaisle_sfi_generalfs',
  value: false
};

export { DualPath };